import { TrialBanner } from "@/pages/BillingPages";
import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { BookOpen, CreditCard, FileText, History, LayoutDashboard, LogOut, Palette, Plus, ShieldCheck, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { BrandLockup } from "@/pages/WelcomePage";
import { InkCharacter } from "@/components/InkCharacter";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/history", label: "Consultations", icon: History, id: "nav-history" },
  { to: "/reports", label: "Reports", icon: FileText, id: "nav-reports" },
  { to: "/kit", label: "My Kit", icon: Palette, id: "nav-kit" },
  { to: "/library", label: "Pigment Library", icon: BookOpen, id: "nav-library" },
  { to: "/profile", label: "Profile", icon: UserRound, id: "nav-profile" },
  { to: "/billing", label: "My Plan", icon: CreditCard, id: "nav-billing" },
];

function VerifyBanner() {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent — check your inbox");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div role="status" data-testid="verify-email-banner" className="print:hidden flex flex-wrap items-center gap-3 border-b border-[#efdcbc] bg-[#FAF2E6] px-6 py-3 text-sm text-[#5c3a10]">
      <span className="flex-1">Please confirm your email address — we sent you a verification link.</span>
      <button onClick={resend} disabled={busy} data-testid="verify-resend-btn" className="min-h-[40px] rounded-full border border-[#9C6218]/40 px-4 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-white/60 disabled:opacity-50">
        {busy ? "Sending…" : "Resend email"}
      </button>
    </div>
  );
}

export function useStartConsultation() {
  const navigate = useNavigate();
  return () => navigate("/consult/new");
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 whitespace-nowrap rounded-full px-4 py-2.5 text-sm transition-colors duration-200 ${
    isActive ? "bg-[#FDFBF7] text-[#2C1810] font-semibold" : "text-[#E6DBCF] hover:bg-white/10 hover:text-white"
  }`;

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const start = useStartConsultation();
  const nav = user?.role === "admin" ? [...NAV, { to: "/admin", label: "Owner Admin", icon: ShieldCheck, id: "nav-admin" }] : NAV;

  return (
    <div className="flex min-h-screen flex-col bg-[#FDFBF7] lg:flex-row">
      <aside className="ig-grain relative z-10 flex print:hidden shrink-0 flex-col bg-[#2C1810] px-5 py-5 lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:px-6 lg:py-8">
        <div className="flex items-center justify-between lg:block">
          <div className="rounded-xl bg-[#FDFBF7] px-3 py-1.5 lg:hidden" data-testid="mobile-header-logo"><BrandLockup className="!h-10" /></div>
          <div className="hidden justify-center rounded-2xl bg-[#FDFBF7] px-3 py-3 lg:flex" data-testid="sidebar-logo">
            <InkCharacter size="sm" className="!h-36" />
          </div>
          <button data-testid="mobile-logout-btn" onClick={logout} className="text-[#E8BDB5] lg:hidden" aria-label="Sign out">
            <LogOut size={18} />
          </button>
        </div>
        <button
          data-testid="sidebar-new-consultation-btn"
          onClick={start}
          className="mt-5 flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-5 py-3 text-sm font-semibold text-[#2C1810] transition-colors duration-200 hover:bg-[#d4b273] lg:mt-10"
        >
          <Plus size={16} /> New consultation
        </button>
        <nav data-testid="main-nav" className="-mx-5 mt-4 flex gap-1 overflow-x-auto px-5 pb-1 [mask-image:linear-gradient(to_right,black_85%,transparent)] lg:mx-0 lg:mt-8 lg:px-0 lg:[mask-image:none] lg:flex-col lg:overflow-visible">
          {nav.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={id}>
              <Icon size={16} strokeWidth={1.6} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto hidden border-t border-white/10 pt-5 lg:block">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#A88B7D]">{user?.role === "admin" ? "Owner" : "Artist"}</p>
          <p className="mt-1 truncate text-sm text-[#FDFBF7]" data-testid="sidebar-user-email">{user?.email}</p>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#E8BDB5] transition-colors hover:text-white"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1"><TrialBanner />{user && !user.email_verified && <VerifyBanner />}{children}</main>
    </div>
  );
}
